import React, { useCallback, useState } from 'react';
import { withTranslation } from 'react-i18next';
import useSWR from 'swr';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
} from '@mui/material';

import SparaButton from 'components/SparaButton/SparaButton';
import SparaLoader from 'components/SparaLoader/SparaLoader';
import api, { authApi, fetcher } from 'lib/api';
import axios from 'store/middlewares/axios';
import { generateCodeVerifier, generateCodeChallenge } from 'utils/pkce';
import {
    createPasskey,
    isUserCancelledError,
    isWebAuthnAvailable,
} from 'utils/webauthn';
import styles from './Passkeys.module.scss';

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 95000;

function formatDate(value, locale) {
    if (!value) return '—';
    try {
        return new Date(value).toLocaleString(locale === 'en' ? 'en-GB' : 'is-IS', {
            dateStyle: 'medium',
            timeStyle: 'short',
        });
    } catch {
        return value;
    }
}

function hasKennitala(value) {
    return /^\d{10}$/.test(String(value || '').replace(/\D/g, ''));
}

function hasPhone(value) {
    return String(value || '').replace(/\D/g, '').length >= 7;
}

async function pollAudkenni(initialToken) {
    let currentToken = initialToken;
    const startTime = Date.now();

    while (Date.now() - startTime < POLL_TIMEOUT_MS) {
        await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));

        const pollResult = await axios({
            url: '/api/auth/audkenni/poll',
            method: 'POST',
            data: { token: currentToken },
            timeout: 10000,
        });

        currentToken = pollResult.data.token ?? currentToken;
        const { status } = pollResult.data;

        if (status === 'authenticated') return { status, token: currentToken };
        if (status === 'failed') {
            return {
                status,
                token: currentToken,
                message: pollResult.data.message || pollResult.data.error,
            };
        }
    }

    return { status: 'timeout', token: currentToken };
}

function Passkeys({
    t,
    i18n,
    onNotify,
    userPhone = '',
    userKennitala = '',
    hasPassword = false,
}) {
    const supported = isWebAuthnAvailable();
    const { data, error, mutate, isLoading } = useSWR(authApi.passkeys, fetcher);
    const [isRegistering, setIsRegistering] = useState(false);
    const [deletingId, setDeletingId] = useState(null);
    const [renameDialog, setRenameDialog] = useState({
        open: false,
        id: null,
        name: '',
        isSubmitting: false,
        error: '',
    });
    const [reauthDialog, setReauthDialog] = useState({
        open: false,
        purpose: 'register', // register | delete
        pendingId: null,
        method: 'password', // password | phone | kennitala
        password: '',
        error: '',
        isSubmitting: false,
        vcode: null,
    });

    const credentials = data?.results || [];
    const canPassword = !!hasPassword;
    const canAudkenniPhone = hasPhone(userPhone);
    const canAudkenniKennitala = hasKennitala(userKennitala);

    const defaultReauthMethod = () => {
        if (canPassword) return 'password';
        if (canAudkenniPhone) return 'phone';
        if (canAudkenniKennitala) return 'kennitala';
        return 'password';
    };

    const notify = useCallback(
        (message, type = 'success') => {
            if (onNotify) onNotify(message, type);
        },
        [onNotify]
    );

    const openReauthDialog = (purpose = 'register', pendingId = null) => {
        if (purpose === 'register' && !supported) {
            notify(t('profile.passkey_unsupported'), 'warning');
            return;
        }
        if (!canPassword && !canAudkenniPhone && !canAudkenniKennitala) {
            notify(t('profile.passkey_reauth_no_methods'), 'warning');
            return;
        }
        setReauthDialog({
            open: true,
            purpose,
            pendingId,
            method: defaultReauthMethod(),
            password: '',
            error: '',
            isSubmitting: false,
            vcode: null,
        });
    };

    const closeReauthDialog = () => {
        if (reauthDialog.isSubmitting) return;
        setReauthDialog((prev) => ({
            ...prev,
            open: false,
            pendingId: null,
            password: '',
            error: '',
            vcode: null,
        }));
    };

    const registerWithReauthToken = async (reauthToken) => {
        setIsRegistering(true);
        try {
            const optionsRes = await api.post(authApi.passkeyRegisterOptions, {
                reauth_token: reauthToken,
            });
            const { challenge_id: challengeId, publicKey } = optionsRes.data.results || {};
            if (!challengeId || !publicKey) {
                notify(t('profile.passkey_register_error'), 'warning');
                return;
            }

            const credential = await createPasskey(publicKey);
            await api.post(authApi.passkeyRegister, {
                challenge_id: challengeId,
                credential,
            });

            await mutate();
            notify(t('profile.passkey_register_success'));
        } catch (err) {
            if (isUserCancelledError(err)) {
                notify(t('profile.passkey_cancelled'), 'warning');
            } else {
                const msg =
                    err?.response?.data?.message || t('profile.passkey_register_error');
                notify(msg, 'warning');
            }
        } finally {
            setIsRegistering(false);
        }
    };

    const deleteWithReauthToken = async (reauthToken, id) => {
        setDeletingId(id);
        try {
            await api.delete(authApi.passkeyDelete(id), {
                data: { reauth_token: reauthToken },
            });
            await mutate();
            notify(t('profile.passkey_delete_success'));
        } catch (err) {
            const msg = err?.response?.data?.message || t('profile.passkey_delete_error');
            notify(msg, 'warning');
        } finally {
            setDeletingId(null);
        }
    };

    const continueAfterReauth = async (reauthToken) => {
        if (reauthDialog.purpose === 'delete') {
            await deleteWithReauthToken(reauthToken, reauthDialog.pendingId);
        } else {
            await registerWithReauthToken(reauthToken);
        }
    };

    const handlePasswordReauth = async () => {
        if (!reauthDialog.password) {
            setReauthDialog((prev) => ({
                ...prev,
                error: t('profile.passkey_reauth_password_required'),
            }));
            return;
        }

        setReauthDialog((prev) => ({ ...prev, isSubmitting: true, error: '' }));
        try {
            const res = await api.post(authApi.passkeyReauthPassword, {
                password: reauthDialog.password,
            });
            const reauthToken = res.data?.results?.reauth_token;
            if (!reauthToken) {
                setReauthDialog((prev) => ({
                    ...prev,
                    isSubmitting: false,
                    error: t('profile.passkey_reauth_error'),
                }));
                return;
            }

            setReauthDialog((prev) => ({
                ...prev,
                open: false,
                isSubmitting: false,
                password: '',
                error: '',
            }));
            await continueAfterReauth(reauthToken);
        } catch (err) {
            const msg =
                err?.response?.data?.message || t('profile.passkey_reauth_password_wrong');
            setReauthDialog((prev) => ({
                ...prev,
                isSubmitting: false,
                error: msg,
            }));
        }
    };

    const handleAudkenniReauth = async (method) => {
        setReauthDialog((prev) => ({
            ...prev,
            method,
            isSubmitting: true,
            error: '',
            vcode: null,
        }));

        const codeVerifier = generateCodeVerifier();
        const codeChallenge = await generateCodeChallenge(codeVerifier);

        try {
            const startRes = await api.post(authApi.passkeyReauthAudkenni, {
                method,
                code_challenge: codeChallenge,
            });

            setReauthDialog((prev) => ({
                ...prev,
                vcode: startRes.data?.vcode ?? null,
            }));

            const poll = await pollAudkenni(startRes.data.token);

            if (poll.status === 'timeout') {
                setReauthDialog((prev) => ({
                    ...prev,
                    isSubmitting: false,
                    vcode: null,
                    error: t('login.timeout_error'),
                }));
                return;
            }
            if (poll.status === 'failed') {
                setReauthDialog((prev) => ({
                    ...prev,
                    isSubmitting: false,
                    vcode: null,
                    error: poll.message || t('login.auth_error'),
                }));
                return;
            }

            const completeRes = await api.post(authApi.passkeyReauthAudkenniComplete, {
                token: poll.token,
                code_verifier: codeVerifier,
            });

            const reauthToken = completeRes.data?.results?.reauth_token;
            if (!reauthToken) {
                setReauthDialog((prev) => ({
                    ...prev,
                    isSubmitting: false,
                    vcode: null,
                    error: t('profile.passkey_reauth_error'),
                }));
                return;
            }

            setReauthDialog((prev) => ({
                ...prev,
                open: false,
                isSubmitting: false,
                vcode: null,
                error: '',
            }));
            await continueAfterReauth(reauthToken);
        } catch (err) {
            const msg =
                err?.response?.data?.message ||
                err?.response?.data?.error ||
                t('profile.passkey_reauth_error');
            setReauthDialog((prev) => ({
                ...prev,
                isSubmitting: false,
                vcode: null,
                error: msg,
            }));
        }
    };

    const handleDelete = (id) => {
        if (!window.confirm(t('profile.passkey_delete_confirm'))) return;
        openReauthDialog('delete', id);
    };

    const openRename = (cred) => {
        setRenameDialog({
            open: true,
            id: cred.id,
            name: cred.name || '',
            isSubmitting: false,
            error: '',
        });
    };

    const closeRename = () => {
        setRenameDialog({
            open: false,
            id: null,
            name: '',
            isSubmitting: false,
            error: '',
        });
    };

    const handleRename = async () => {
        const trimmed = renameDialog.name.trim();
        if (!trimmed) {
            setRenameDialog((prev) => ({
                ...prev,
                error: t('profile.passkey_name_required'),
            }));
            return;
        }

        setRenameDialog((prev) => ({ ...prev, isSubmitting: true, error: '' }));
        try {
            await api.put(authApi.passkeyUpdate(renameDialog.id), { name: trimmed });
            await mutate();
            closeRename();
            notify(t('profile.passkey_rename_success'));
        } catch (err) {
            const msg = err?.response?.data?.message || t('profile.passkey_rename_error');
            setRenameDialog((prev) => ({
                ...prev,
                isSubmitting: false,
                error: msg,
            }));
        }
    };

    return (
        <div className={styles.wrap}>
            <p className={styles.intro}>{t('profile.passkey_intro')}</p>

            {!supported && (
                <p className={styles.warning}>{t('profile.passkey_unsupported')}</p>
            )}

            {isLoading && (
                <div className={styles.loaderWrap}>
                    <SparaLoader text={t('common.loading')} />
                </div>
            )}

            {error && !isLoading && (
                <p className={styles.warning}>{t('profile.passkey_load_error')}</p>
            )}

            {!isLoading && !error && credentials.length === 0 && (
                <p className={styles.empty}>{t('profile.passkey_empty')}</p>
            )}

            <ul className={styles.list}>
                {credentials.map((cred) => (
                    <li key={cred.id} className={styles.item}>
                        <div className={styles.itemMain}>
                            <span className={styles.itemName}>
                                {cred.name || t('profile.passkey_unnamed')}
                            </span>
                            <span className={styles.itemMeta}>
                                {t('profile.passkey_created')}:{' '}
                                {formatDate(cred.created_at, i18n.language)}
                                {cred.last_used_at
                                    ? ` · ${t('profile.passkey_last_used')}: ${formatDate(
                                          cred.last_used_at,
                                          i18n.language
                                      )}`
                                    : ''}
                            </span>
                        </div>
                        <div className={styles.itemActions}>
                            <button
                                type="button"
                                className={styles.linkBtn}
                                onClick={() => openRename(cred)}
                            >
                                {t('profile.passkey_rename')}
                            </button>
                            <button
                                type="button"
                                className={styles.linkBtnDanger}
                                onClick={() => handleDelete(cred.id)}
                                disabled={deletingId === cred.id}
                            >
                                {deletingId === cred.id
                                    ? t('common.saving')
                                    : t('profile.passkey_delete')}
                            </button>
                        </div>
                    </li>
                ))}
            </ul>

            <div className={styles.addWrap}>
                <SparaButton
                    onClick={() => openReauthDialog('register')}
                    loading={isRegistering}
                    disabled={!supported}
                >
                    {t('profile.passkey_add')}
                </SparaButton>
            </div>

            <Dialog
                open={reauthDialog.open}
                onClose={closeReauthDialog}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>{t('profile.passkey_reauth_title')}</DialogTitle>
                <DialogContent>
                    <p className={styles.reauthIntro}>{t('profile.passkey_reauth_intro')}</p>

                    <div className={styles.reauthMethods}>
                        {canPassword && (
                            <button
                                type="button"
                                className={`${styles.reauthTab} ${
                                    reauthDialog.method === 'password'
                                        ? styles.reauthTabActive
                                        : ''
                                }`}
                                onClick={() =>
                                    setReauthDialog((prev) => ({
                                        ...prev,
                                        method: 'password',
                                        error: '',
                                        vcode: null,
                                    }))
                                }
                                disabled={reauthDialog.isSubmitting}
                            >
                                {t('profile.passkey_reauth_password')}
                            </button>
                        )}
                        {canAudkenniPhone && (
                            <button
                                type="button"
                                className={`${styles.reauthTab} ${
                                    reauthDialog.method === 'phone' ? styles.reauthTabActive : ''
                                }`}
                                onClick={() =>
                                    setReauthDialog((prev) => ({
                                        ...prev,
                                        method: 'phone',
                                        error: '',
                                        password: '',
                                        vcode: null,
                                    }))
                                }
                                disabled={reauthDialog.isSubmitting}
                            >
                                {t('profile.passkey_reauth_phone')}
                            </button>
                        )}
                        {canAudkenniKennitala && (
                            <button
                                type="button"
                                className={`${styles.reauthTab} ${
                                    reauthDialog.method === 'kennitala'
                                        ? styles.reauthTabActive
                                        : ''
                                }`}
                                onClick={() =>
                                    setReauthDialog((prev) => ({
                                        ...prev,
                                        method: 'kennitala',
                                        error: '',
                                        password: '',
                                        vcode: null,
                                    }))
                                }
                                disabled={reauthDialog.isSubmitting}
                            >
                                {t('profile.passkey_reauth_kennitala')}
                            </button>
                        )}
                    </div>

                    {reauthDialog.method === 'password' ? (
                        <TextField
                            autoFocus
                            margin="dense"
                            label={t('login.password_label')}
                            type="password"
                            fullWidth
                            variant="standard"
                            value={reauthDialog.password}
                            onChange={(e) =>
                                setReauthDialog((prev) => ({
                                    ...prev,
                                    password: e.target.value,
                                    error: '',
                                }))
                            }
                            onKeyDown={(e) => e.key === 'Enter' && handlePasswordReauth()}
                            error={!!reauthDialog.error}
                            disabled={reauthDialog.isSubmitting}
                        />
                    ) : (
                        <p className={styles.reauthHint}>
                            {reauthDialog.method === 'phone'
                                ? t('profile.passkey_reauth_phone_hint')
                                : t('profile.passkey_reauth_kennitala_hint')}
                        </p>
                    )}

                    {reauthDialog.vcode != null && reauthDialog.isSubmitting && (
                        <div className={styles.vcodeBox}>
                            {t('login.security_code')}
                            <b translate="no">
                                {String(reauthDialog.vcode).padStart(4, '0')}
                            </b>
                        </div>
                    )}

                    {reauthDialog.error && (
                        <p className={styles.dialogError}>{reauthDialog.error}</p>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeReauthDialog} disabled={reauthDialog.isSubmitting}>
                        {t('cancel')}
                    </Button>
                    {reauthDialog.method === 'password' ? (
                        <Button
                            onClick={handlePasswordReauth}
                            disabled={reauthDialog.isSubmitting}
                            variant="contained"
                            color="error"
                        >
                            {reauthDialog.isSubmitting
                                ? t('common.saving')
                                : t('profile.passkey_reauth_continue')}
                        </Button>
                    ) : (
                        <Button
                            onClick={() => handleAudkenniReauth(reauthDialog.method)}
                            disabled={reauthDialog.isSubmitting}
                            variant="contained"
                            color="error"
                        >
                            {reauthDialog.isSubmitting
                                ? t('common.saving')
                                : t('profile.passkey_reauth_continue')}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            <Dialog open={renameDialog.open} onClose={closeRename} maxWidth="xs" fullWidth>
                <DialogTitle>{t('profile.passkey_rename')}</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label={t('profile.passkey_name')}
                        type="text"
                        fullWidth
                        variant="standard"
                        value={renameDialog.name}
                        onChange={(e) =>
                            setRenameDialog((prev) => ({
                                ...prev,
                                name: e.target.value,
                                error: '',
                            }))
                        }
                        onKeyDown={(e) => e.key === 'Enter' && handleRename()}
                        error={!!renameDialog.error}
                    />
                    {renameDialog.error && (
                        <p className={styles.dialogError}>{renameDialog.error}</p>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeRename} disabled={renameDialog.isSubmitting}>
                        {t('cancel')}
                    </Button>
                    <Button
                        onClick={handleRename}
                        disabled={renameDialog.isSubmitting}
                        variant="contained"
                        color="error"
                    >
                        {renameDialog.isSubmitting ? t('common.saving') : t('save')}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}

export default withTranslation('', { withRef: true })(Passkeys);
